import React, { createContext, useState, useContext, useEffect } from 'react';
import { ar } from '../translations/ar';
import { en } from '../translations/en';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(localStorage.getItem('language') || 'ar');
    const [numberFormat, setNumberFormat] = useState(localStorage.getItem('system_number_format') || 'english');
    const [translations, setTranslations] = useState(language === 'ar' ? ar : en);

    useEffect(() => {
        setTranslations(language === 'ar' ? ar : en);
        localStorage.setItem('language', language);

        // Update document direction
        document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
        
        // Update document lang attribute based on language and numberFormat
        if (language === 'ar') {
            if (numberFormat === 'arabic') {
                document.documentElement.setAttribute('lang', 'ar-SA');
            } else {
                // Force en-US to make browser native date pickers and inputs display Western/Latin digits
                document.documentElement.setAttribute('lang', 'en-US');
            }
        } else {
            document.documentElement.setAttribute('lang', language);
        }
    }, [language, numberFormat]);

    useEffect(() => {
        localStorage.setItem('system_number_format', numberFormat);
        window.dispatchEvent(new Event('system_date_time_format_changed'));

        // Update document classes for WebKit-locale digit overrides on native inputs
        if (numberFormat === 'arabic') {
            document.documentElement.classList.add('number-format-arabic');
            document.documentElement.classList.remove('number-format-english');
        } else {
            document.documentElement.classList.add('number-format-english');
            document.documentElement.classList.remove('number-format-arabic');
        }
    }, [numberFormat]);

    const t = (key, params = {}) => {
        let translation = translations[key] || key;

        if (params && typeof params === 'object') {
            Object.keys(params).forEach((param) => {
                translation = translation.replace(`{${param}}`, params[param]);
            });
        }

        return translation;
    };

    const toggleLanguage = () => {
        setLanguage((prev) => (prev === 'ar' ? 'en' : 'ar'));
    };

    return (
        <LanguageContext.Provider
            value={{ language, setLanguage, toggleLanguage, t, isRtl: language === 'ar', numberFormat, setNumberFormat }}
        >
            {children}
        </LanguageContext.Provider>
    );
};
